import React, { useMemo, useState } from "react";
import { ArrowUpDown, Download, Search, Plus } from "lucide-react";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { toCsv } from "../lib/format";

/**
 * columns: [{ key, label, render?, align?, className? }]
 */
export const DataTable = ({
  columns, rows, testid = "data-table", title, searchable = true, onAdd, addLabel = "New",
  csvName = "export", rowKey = "id", onRowClick, expandable, dense = true, right,
}) => {
  const [q, setQ] = useState("");
  const [sort, setSort] = useState({ key: null, dir: 1 });
  const [expanded, setExpanded] = useState(null);

  const filtered = useMemo(() => {
    let out = rows || [];
    if (q) {
      const s = q.toLowerCase();
      out = out.filter((r) => columns.some((c) => String(r[c.key] ?? "").toLowerCase().includes(s)));
    }
    if (sort.key) {
      out = [...out].sort((a, b) => {
        const av = a[sort.key], bv = b[sort.key];
        if (typeof av === "number" && typeof bv === "number") return (av - bv) * sort.dir;
        return String(av ?? "").localeCompare(String(bv ?? "")) * sort.dir;
      });
    }
    return out;
  }, [rows, q, sort, columns]);

  const toggleSort = (key) => setSort((s) => ({ key, dir: s.key === key ? -s.dir : 1 }));

  return (
    <div data-testid={testid} className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg shadow-sm">
      {(title || searchable || onAdd || right) && (
        <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-slate-200 dark:border-slate-800">
          <h3 className="text-base font-bold text-slate-900 dark:text-slate-100">{title}</h3>
          <div className="flex items-center gap-2">
            {searchable && (
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input data-testid={`${testid}-search`} value={q} onChange={(e) => setQ(e.target.value)}
                  placeholder="Search here" className="pl-8 h-9 w-52 bg-slate-50 dark:bg-slate-800" />
              </div>
            )}
            <Button data-testid={`${testid}-export`} variant="outline" size="sm" onClick={() => toCsv(filtered, csvName)}>
              <Download className="h-4 w-4" />
            </Button>
            {right}
            {onAdd && (
              <Button data-testid={`${testid}-add`} size="sm" className="bg-orange-600 hover:bg-orange-700 text-white" onClick={onAdd}>
                <Plus className="h-4 w-4 mr-1" /> {addLabel}
              </Button>
            )}
          </div>
        </div>
      )}
      <div className="overflow-x-auto erp-scroll">
        <table className="w-full erp-table">
          <thead className="bg-slate-100/80 dark:bg-slate-800/80 sticky top-0 z-10">
            <tr>
              {expandable && <th className="w-8" />}
              {columns.map((c) => (
                <th key={c.key} onClick={() => toggleSort(c.key)}
                  className={`px-3 py-2.5 cursor-pointer select-none ${c.align === "right" ? "text-right" : "text-left"}`}>
                  <span className="inline-flex items-center gap-1">{c.label}<ArrowUpDown className="h-3 w-3 opacity-40" /></span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={columns.length + (expandable ? 1 : 0)} className="px-3 py-10 text-center text-sm text-slate-400">No records</td></tr>
            )}
            {filtered.map((r, i) => (
              <React.Fragment key={r[rowKey] || i}>
                <tr
                  data-testid={`${testid}-row-${i}`}
                  onClick={() => { onRowClick?.(r); if (expandable) setExpanded(expanded === i ? null : i); }}
                  className={`border-b border-slate-100 dark:border-slate-800/60 hover:bg-orange-50/50 dark:hover:bg-slate-800/40 transition-colors ${(onRowClick || expandable) ? "cursor-pointer" : ""}`}
                >
                  {expandable && (
                    <td className="px-2 text-slate-400">
                      <span className={`inline-block transition-transform ${expanded === i ? "rotate-90" : ""}`}>▸</span>
                    </td>
                  )}
                  {columns.map((c) => (
                    <td key={c.key} className={`px-3 ${dense ? "py-2" : "py-3"} text-sm text-slate-700 dark:text-slate-300 ${c.align === "right" ? "text-right font-mono tabular-nums" : ""} ${c.className || ""}`}>
                      {c.render ? c.render(r) : r[c.key]}
                    </td>
                  ))}
                </tr>
                {expandable && expanded === i && (
                  <tr className="bg-slate-50 dark:bg-slate-800/30">
                    <td colSpan={columns.length + 1} className="px-6 py-4">{expandable(r)}</td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DataTable;
